import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';

import { useLogoImage } from '@app/hooks/useLogoImage';
import { FIELD_TYPES, validateFormFields } from '@app/utils/validateUtils';

import { DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';
import EditView from '@app/components/common/views/EditView';

import { ExpertForm } from './ExpertForm';

const formFields = [
  { key: 'first_name', type: FIELD_TYPES.MULTILINGUAL, message: 'First name is required' },
  { key: 'last_name', type: FIELD_TYPES.MULTILINGUAL, message: 'Last name is required' },
  { key: 'expert_roles', type: FIELD_TYPES.MULTILINGUAL, message: 'Role is required' },
  { key: 'email', type: FIELD_TYPES.EMAIL, message: 'Email is not valid', nonRequired: true },
];

const nonRequiredFields = [
  { key: 'about', emptyValue: '', isMultilingual: true },
  { key: 'twitterurl', emptyValue: '', isMultilingual: false },
  { key: 'linked_inurl', emptyValue: '', isMultilingual: false },
  { key: 'company_id', emptyValue: 0 },
  { key: 'expertise_ids', emptyValue: [] },
];

export const EditExpert = () => {
  const dataProvider = useDataProvider();
  const { logoImage, handleChangeImageLogo, transform } = useLogoImage(nonRequiredFields);

  const validate = useCallback(
    (values: GenericObject) => validateFormFields({ fields: formFields, values, image: logoImage }),
    [logoImage]
  );

  const resolveDeleteConfirmationContent = async (companyId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('experts', companyId);
    const links = response?.data;
    return {
      deletable: true,
      message:
        links.Subscribers > 0
          ? `This expert is currently being followed by ${links.Subscribers} on the website. Are you sure you want to delete this expert?`
          : 'Are you sure you want to delete this expert?',
    } as DeleteConfirmation;
  };

  return (
    <div>
      <EditView
        resolveDeleteConfirmation={resolveDeleteConfirmationContent}
        resourceSingular="expert"
        validate={validate}
        transform={transform}
        mutationMode="pessimistic">
        <ExpertForm expertLogo={logoImage} title="EDIT EXPERT" handleChangeExpertLogo={handleChangeImageLogo} />
      </EditView>
    </div>
  );
};
